import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
} from '@mui/material';
import {
  People as PeopleIcon,
  SmartToy as SmartToyIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  CloudQueue as CloudIcon,
  TrendingUp as TrendingUpIcon,
  PersonAddAlt as PersonAddAltIcon,
  Email as EmailIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { Vendor } from '../types';
import { MagicBackground } from '../components/MagicBackground';
import { isAdminUser } from '../utils/auth';

interface AdminStats {
  totalVendors: number;
  activeVendors: number;
  totalCustomers: number;
  activeAiAgents: number;
  totalRevenue: number;
  aiUsage: {
    totalConversations: number;
    totalConversationsThisMonth?: number;
    totalMessagesThisMonth?: number;
    totalTokensUsedThisMonth?: number;
    averageMessagesPerConversation: number;
    topPerformingAgents: { 
      vendorId: string; 
      conversations: number;
      messages?: number;
      tokens?: number;
    }[];
  };
  usageByTier?: {
    'side-hustle': {
      count: number;
      conversationsThisMonth: number;
      messagesThisMonth: number;
    };
    'local-legend': {
      count: number;
      conversationsThisMonth: number;
      messagesThisMonth: number;
    };
    'empire': {
      count: number;
      conversationsThisMonth: number;
      messagesThisMonth: number;
    };
  };
  systemHealth: {
    apiLatency: string;
    lambdaErrors: string;
    dynamoDbThrottles: string;
  };
  vendorGrowth: {
    newVendorsLastMonth: number;
    churnRate: string;
  };
  recentSignups?: Array<{
    name: string;
    email: string;
    tier: string;
    date: string;
    usage?: {
      conversationsThisMonth: number;
      messagesThisMonth: number;
    };
  }>;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const fetchAdminData = async () => {
    setLoading(true);
    setError(null);
    try {
      const statsResponse = await apiClient.get('/admin/stats');
      setStats(statsResponse.data);

      const vendorsResponse = await apiClient.get('/vendors');
      setVendors(Array.isArray(vendorsResponse.data) ? vendorsResponse.data : []);
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
      setError(err.response?.data?.message || 'Failed to load admin data');
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is admin
    const checkAdminAccess = async () => {
      const isAdmin = await isAdminUser();
      if (!isAdmin) {
        setError('Access denied. Admin privileges required.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
        return;
      }
      fetchAdminData();
    };
    
    checkAdminAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleImpersonate = async (vendorId: string) => {
    try {
      localStorage.setItem('impersonateVendorId', vendorId);
      setSnackbar({ open: true, message: `Now impersonating vendor: ${vendorId}`, severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Failed to impersonate vendor', severity: 'error' });
    }
  };

  const handleStopImpersonation = () => {
    localStorage.removeItem('impersonateVendorId');
    setSnackbar({ open: true, message: 'Impersonation stopped', severity: 'success' });
    fetchAdminData(); // Refresh to clear impersonation state
  };

  const handleInviteAdmin = async () => {
    if (!inviteForm.email || !inviteForm.firstName) {
      setSnackbar({ open: true, message: 'Email and First Name are required', severity: 'error' });
      return;
    }

    setInviteLoading(true);
    try {
      await apiClient.post('/admin/invite', {
        email: inviteForm.email,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName || undefined,
      });
      setSnackbar({ open: true, message: `Invitation sent to ${inviteForm.email}`, severity: 'success' });
      setInviteDialogOpen(false);
      setInviteForm({ email: '', firstName: '', lastName: '' });
    } catch (err: any) {
      console.error('Error inviting admin:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to send invitation', severity: 'error' });
    } finally {
      setInviteLoading(false);
    }
  };

  const filteredVendors = vendors.filter(
    (vendor) =>
      vendor.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor.vendorId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    gradient: string;
    subtitle?: string;
  }> = ({ title, value, icon, gradient, subtitle }) => (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${gradient})`,
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 3,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
        },
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          {icon}
        </Box>
        <Typography variant="h2" fontWeight="bold" color="white" sx={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          {value}
        </Typography>
        <Typography variant="body1" color="rgba(255,255,255,0.9)" mt={1} fontWeight={500}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="rgba(255,255,255,0.7)" mt={0.5} display="block">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea' }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box mb={6} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography 
              variant="h2" 
              fontWeight="bold" 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Mission Control
            </Typography>
            <Typography variant="h6" color="rgba(255,255,255,0.7)">
              ReachOut Platform Administration
            </Typography>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            {localStorage.getItem('impersonateVendorId') && (
              <Button
                variant="outlined"
                onClick={handleStopImpersonation}
                startIcon={<WarningIcon />}
                sx={{
                  borderColor: '#f093fb',
                  color: '#f093fb',
                  background: 'rgba(240, 147, 251, 0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    borderColor: '#f093fb',
                    background: 'rgba(240, 147, 251, 0.2)',
                  },
                }}
              >
                Stop Impersonation
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => navigate('/missioncontrol/phone-numbers')}
              sx={{
                borderColor: '#00D4FF',
                color: '#00D4FF',
                background: 'rgba(0, 212, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: '#00D4FF',
                  background: 'rgba(0, 212, 255, 0.2)',
                },
              }}
            >
              📞 Phone Numbers
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setInviteDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #00C896 0%, #38F9D7 100%)',
                boxShadow: '0 4px 20px rgba(0, 200, 150, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #00B085 0%, #2EE6C4 100%)',
                  boxShadow: '0 6px 30px rgba(0, 200, 150, 0.6)',
                },
              }}
            >
              Invite Admin
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchAdminData}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 30px rgba(102, 126, 234, 0.6)',
                },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              background: 'rgba(211, 47, 47, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(211, 47, 47, 0.3)',
              color: '#ff6b6b',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Stats Grid */}
        {stats && (
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Total Vendors"
                value={stats.totalVendors}
                icon={<PeopleIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
                gradient="rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.1)"
                subtitle={`${stats.activeVendors} active`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Total Customers"
                value={(stats.totalCustomers || 0).toLocaleString()}
                icon={<PersonAddIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
                gradient="rgba(240, 147, 251, 0.2), rgba(245, 87, 108, 0.1)"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Active AI Agents"
                value={stats.activeAiAgents || 0}
                icon={<SmartToyIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
                gradient="rgba(79, 172, 254, 0.2), rgba(0, 242, 254, 0.1)"
                subtitle={`${(stats.aiUsage?.totalConversations || 0).toLocaleString()} conversations`}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Total Revenue"
                value={`$${(stats.totalRevenue || 0).toLocaleString()}`}
                icon={<MoneyIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
                gradient="rgba(67, 233, 123, 0.2), rgba(56, 249, 215, 0.1)"
              />
            </Grid>
          </Grid>
        )}

        {/* Tabs */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{
              '& .MuiTab-root': { 
                color: 'rgba(255,255,255,0.6)',
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
              },
              '& .Mui-selected': { 
                color: 'white !important',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.1) 100%)',
              },
              '& .MuiTabs-indicator': { 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                height: 3,
              },
            }}
          >
            <Tab label="🎯 Vendors" />
            <Tab label="💚 System Health" />
            <Tab label="🤖 AI Usage" />
            <Tab label="📈 Growth" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 4,
            }}
          >
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography variant="h4" fontWeight="bold" color="white">
                All Vendors
              </Typography>
              <TextField
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', sm: 350 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(0,0,0,0.3)',
                    color: 'white',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Business Name</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Vendor ID</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Email</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Industry</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Status</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Tier</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Usage (This Month)</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVendors.map((vendor) => (
                    <TableRow 
                      key={vendor.vendorId} 
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.03)',
                        },
                      }}
                    >
                      <TableCell sx={{ color: 'white', fontWeight: 500, borderColor: 'rgba(255,255,255,0.1)' }}>
                        {vendor.businessName}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.1)' }}>
                        {vendor.vendorId}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>
                        {vendor.email}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.1)' }}>
                        {vendor.industry || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        <Chip
                          label={vendor.status}
                          size="small"
                          icon={vendor.status === 'active' ? <CheckCircleIcon /> : undefined}
                          sx={{
                            bgcolor: vendor.status === 'active' ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                            color: vendor.status === 'active' ? '#43e97b' : 'rgba(255,255,255,0.6)',
                            border: `1px solid ${vendor.status === 'active' ? 'rgba(67, 233, 123, 0.3)' : 'rgba(255,255,255,0.2)'}`,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        <Chip
                          label={vendor.subscriptionTier}
                          size="small"
                          sx={{
                            background:
                              vendor.subscriptionTier === 'empire'
                                ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(255, 165, 0, 0.2) 100%)'
                                : vendor.subscriptionTier === 'local-legend'
                                ? 'linear-gradient(135deg, rgba(123, 97, 255, 0.3) 0%, rgba(106, 81, 227, 0.2) 100%)'
                                : vendor.subscriptionTier === 'side-hustle'
                                ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.3) 0%, rgba(0, 184, 230, 0.2) 100%)'
                                : 'linear-gradient(135deg, rgba(67, 233, 123, 0.3) 0%, rgba(56, 249, 215, 0.2) 100%)',
                            color: 'white',
                            fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}>
                        <Box>
                          <Typography variant="body2" color="rgba(255,255,255,0.7)">
                            Convos: {vendor.usageMetrics?.conversationsThisMonth || 0}
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.7)">
                            Msgs: {vendor.usageMetrics?.messagesThisMonth || 0}
                          </Typography>
                          <Typography variant="caption" color="rgba(255,255,255,0.5)">
                            Tokens: {(vendor.usageMetrics?.tokensUsedThisMonth || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleImpersonate(vendor.vendorId)}
                          sx={{
                            borderColor: 'rgba(102, 126, 234, 0.5)',
                            color: '#667eea',
                            background: 'rgba(102, 126, 234, 0.1)',
                            backdropFilter: 'blur(10px)',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#667eea',
                              background: 'rgba(102, 126, 234, 0.2)',
                              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                            },
                          }}
                        >
                          Impersonate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredVendors.length === 0 && (
              <Box py={6} textAlign="center">
                <Typography color="rgba(255,255,255,0.5)" variant="h6">
                  No vendors found
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {currentTab === 1 && stats && (
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 4,
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="white" mb={4}>
              System Health
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.2), rgba(56, 249, 215, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    borderRadius: 3,
                    p: 3,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <SpeedIcon sx={{ fontSize: 40, color: '#43e97b' }} />
                    <Typography variant="body1" color="rgba(255,255,255,0.8)">
                      API Latency
                    </Typography>
                  </Box>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {stats.systemHealth?.apiLatency || 'N/A'}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: 3,
                    p: 3,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <WarningIcon sx={{ fontSize: 40, color: '#667eea' }} />
                    <Typography variant="body1" color="rgba(255,255,255,0.8)">
                      Lambda Errors
                    </Typography>
                  </Box>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {stats.systemHealth?.lambdaErrors || '0'}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.2), rgba(245, 87, 108, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(240, 147, 251, 0.3)',
                    borderRadius: 3,
                    p: 3,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CloudIcon sx={{ fontSize: 40, color: '#f093fb' }} />
                    <Typography variant="body1" color="rgba(255,255,255,0.8)">
                      DynamoDB Throttles
                    </Typography>
                  </Box>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {stats.systemHealth?.dynamoDbThrottles || '0'}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {currentTab === 2 && stats && (
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 4,
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="white" mb={4}>
              AI Usage Metrics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.2), rgba(0, 242, 254, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(79, 172, 254, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 48, color: '#4facfe', mb: 2 }} />
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Total Conversations
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {(stats.aiUsage?.totalConversations || 0).toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Avg Messages per Conversation
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {(stats.aiUsage?.averageMessagesPerConversation || 0).toFixed(1)}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.2), rgba(56, 249, 215, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Conversations This Month
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {(stats.aiUsage?.totalConversationsThisMonth || 0).toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Messages This Month
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {(stats.aiUsage?.totalMessagesThisMonth || 0).toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(123, 97, 255, 0.2), rgba(106, 81, 227, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(123, 97, 255, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Tokens Used This Month
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {(stats.aiUsage?.totalTokensUsedThisMonth || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.6)" mt={1}>
                    Est. Cost: ${((stats.aiUsage?.totalTokensUsedThisMonth || 0) * 0.000035 / 1000).toFixed(2)}
                  </Typography>
                </Card>
              </Grid>
              {stats.usageByTier && (
                <Grid item xs={12}>
                  <Typography variant="h5" color="white" mb={2} fontWeight={600}>
                    Usage by Subscription Tier
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{
                        background: 'rgba(0, 212, 255, 0.1)',
                        border: '1px solid rgba(0, 212, 255, 0.3)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Typography variant="h6" color="#00D4FF" mb={1}>The Side Hustle</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Vendors: {stats.usageByTier['side-hustle']?.count || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Convos: {stats.usageByTier['side-hustle']?.conversationsThisMonth || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Messages: {stats.usageByTier['side-hustle']?.messagesThisMonth || 0}</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{
                        background: 'rgba(123, 97, 255, 0.1)',
                        border: '1px solid rgba(123, 97, 255, 0.3)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Typography variant="h6" color="#7B61FF" mb={1}>The Local Legend</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Vendors: {stats.usageByTier['local-legend']?.count || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Convos: {stats.usageByTier['local-legend']?.conversationsThisMonth || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Messages: {stats.usageByTier['local-legend']?.messagesThisMonth || 0}</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{
                        background: 'rgba(255, 215, 0, 0.1)',
                        border: '1px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: 2,
                        p: 2,
                      }}>
                        <Typography variant="h6" color="#FFD700" mb={1}>The Empire</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Vendors: {stats.usageByTier['empire']?.count || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Convos: {stats.usageByTier['empire']?.conversationsThisMonth || 0}</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">Messages: {stats.usageByTier['empire']?.messagesThisMonth || 0}</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="h5" color="white" mb={2} fontWeight={600}>
                  Top Performing Agents
                </Typography>
                <TableContainer sx={{ 
                  background: 'rgba(0,0,0,0.3)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>
                          Business Name
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>
                          Conversations
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>
                          Messages
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>
                          Tokens
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats.aiUsage?.topPerformingAgents || []).map((agent, idx) => {
                        const vendor = vendors.find(v => v.vendorId === agent.vendorId);
                        return (
                          <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                            <TableCell sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.1)' }}>
                              {vendor?.businessName || agent.vendorId}
                            </TableCell>
                            <TableCell sx={{ color: '#4facfe', fontWeight: 'bold', fontSize: '1.1rem', borderColor: 'rgba(255,255,255,0.1)' }}>
                              {agent.conversations.toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.1)' }}>
                              {(agent.messages || 0).toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.1)' }}>
                              {(agent.tokens || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}

        {currentTab === 3 && stats && (
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              p: 4,
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="white" mb={4}>
              Growth Metrics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.2), rgba(56, 249, 215, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 48, color: '#43e97b', mb: 2 }} />
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    New Vendors (Last Month)
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    +{stats.vendorGrowth?.newVendorsLastMonth || 0}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.2), rgba(245, 87, 108, 0.1))',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(240, 147, 251, 0.3)',
                    borderRadius: 3,
                    p: 4,
                  }}
                >
                  <WarningIcon sx={{ fontSize: 48, color: '#f093fb', mb: 2 }} />
                  <Typography variant="body1" color="rgba(255,255,255,0.8)" mb={1}>
                    Churn Rate
                  </Typography>
                  <Typography variant="h2" color="white" fontWeight="bold">
                    {stats.vendorGrowth?.churnRate || '0%'}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Container>

      {/* Invite Admin Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => !inviteLoading && setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(20, 25, 45, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddAltIcon sx={{ color: '#00C896' }} />
            <Typography variant="h6" fontWeight="bold">
              Invite Admin User
            </Typography>
          </Box>
          <IconButton
            onClick={() => setInviteDialogOpen(false)}
            disabled={inviteLoading}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="rgba(255,255,255,0.6)" mb={3}>
            Send an invitation to grant admin access to the ReachOut platform. The user will receive an email with setup instructions.
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              required
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              disabled={inviteLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#00C896' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              }}
            />
            <TextField
              label="First Name"
              fullWidth
              required
              value={inviteForm.firstName}
              onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
              disabled={inviteLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#00C896' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              }}
            />
            <TextField
              label="Last Name (Optional)"
              fullWidth
              value={inviteForm.lastName}
              onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
              disabled={inviteLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#00C896' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setInviteDialogOpen(false)}
            disabled={inviteLoading}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInviteAdmin}
            disabled={inviteLoading || !inviteForm.email || !inviteForm.firstName}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #00C896 0%, #38F9D7 100%)',
              boxShadow: '0 4px 20px rgba(0, 200, 150, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #00B085 0%, #2EE6C4 100%)',
                boxShadow: '0 6px 30px rgba(0, 200, 150, 0.6)',
              },
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {inviteLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            bgcolor: snackbar.severity === 'success' ? 'rgba(0, 200, 150, 0.2)' : 'rgba(211, 47, 47, 0.2)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${snackbar.severity === 'success' ? 'rgba(0, 200, 150, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
            color: 'white',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
